import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';

// Apply stored theme before first render to avoid flash
(function() {
  const t = localStorage.getItem('theme') || 'auto';
  const resolved = t === 'auto'
    ? (window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark')
    : t;
  document.documentElement.setAttribute('data-theme', resolved);
  document.documentElement.setAttribute('data-theme-pref', t);
})();

// Contexts & Providers (always loaded — needed for auth gate)
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { StudentViewProvider } from './components/cfi/StudentViewProvider';

// Shared components (always loaded — visible on every page)
import { Header } from './components/shared/Header';
import { BottomNav } from './components/shared/BottomNav';

// Tab pages — eagerly imported so they mount once and stay alive
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import PlanPage from './pages/PlanPage';
import TrainPage from './pages/train/TrainPage';

// Non-tab pages — lazy loaded (infrequently visited)
const LoginPage        = lazy(() => import('./pages/LoginPage'));
const ProfilePage      = lazy(() => import('./pages/ProfilePage'));
// ORPHANED: const AircraftPage     = lazy(() => import('./pages/AircraftPage'));
const VfrSearchPage    = lazy(() => import('./pages/VfrSearchPage'));
const ProfileWizard    = lazy(() => import('./pages/ProfileWizard'));
const OnboardingPage   = lazy(() => import('./pages/OnboardingPage'));
const FlightDetailPage = lazy(() => import('./pages/FlightDetailPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));

const TAB_PATHS = { flights: '/flights', plan: '/plan', dash: '/dashboard', train: '/train' };
const TAB_ROUTES = new Set(['/flights', '/plan', '/dashboard', '/train']);

function SmartHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!user) { navigate('/login', { replace: true }); return; }
    if (!user.onboarding_complete) { navigate('/onboarding', { replace: true }); return; }
    const order = user?.app_settings?.tab_order || ['flights', 'plan', 'dash', 'train'];
    const firstPath = TAB_PATHS[order[0]] || '/flights';
    navigate(firstPath, { replace: true });
  }, [user, navigate]);
  return null;
}

const PageSpinner = () => (
  <div className="loading"><div className="spinner" /><span>Loading...</span></div>
);

function SuperuserBanner() {
  const { isImpersonating, user } = useAuth();
  if (!isImpersonating || !user) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
      background: '#7c3aed', color: '#fff',
      padding: '6px 16px', fontSize: 13, fontWeight: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      boxShadow: '0 2px 8px rgba(124,58,237,0.5)'
    }}>
      <span>&#128065; Superuser: viewing as <strong>{user.display_name || user.username}</strong> ({user.username})</span>
      <span style={{ fontSize: 11, opacity: 0.8 }}>Token expires in 2h &middot; Close tab to exit</span>
    </div>
  );
}

// Keep-alive tab panels — all four tabs stay mounted, visibility toggled via display
function TabPanels() {
  const location = useLocation();
  const { user } = useAuth();
  const path = location.pathname;
  const isTabRoute = TAB_ROUTES.has(path) || path === '/';

  return (
    <div style={{ display: isTabRoute ? 'block' : 'none' }}>
      <div style={{ display: path === '/flights' ? 'block' : 'none' }}><HomePage /></div>
      <div style={{ display: path === '/dashboard' ? 'block' : 'none' }}>{user && <DashboardPage />}</div>
      <div style={{ display: path === '/plan' ? 'block' : 'none' }}>{user && <PlanPage />}</div>
      <div style={{ display: path === '/train' ? 'block' : 'none' }}>{user && <TrainPage />}</div>
    </div>
  );
}

function AppShell() {
  const { pathname } = useLocation();
  const isOnboarding = pathname === '/onboarding';
  return (
    <div className="app">
      <SuperuserBanner />
      {!isOnboarding && <Header />}
      <div style={isOnboarding
        ? { minHeight: '100dvh' }
        : { paddingBottom: 64, paddingTop: 'calc(env(safe-area-inset-top, 0px) + 57px)' }}>
        {!isOnboarding && <TabPanels />}
        <Suspense fallback={<PageSpinner />}>
          <Routes>
            <Route path="/"             element={<SmartHome />} />
            <Route path="/flights"      element={null} />
            <Route path="/dashboard"    element={null} />
            <Route path="/plan"         element={null} />
            <Route path="/train"        element={null} />
            <Route path="/login"        element={<LoginPage />} />
            <Route path="/reset"        element={<ResetPasswordPage />} />
            <Route path="/onboarding"   element={<OnboardingPage />} />
            <Route path="/profile"      element={<ProfilePage />} />
            {/* ORPHANED: <Route path="/aircraft" element={<AircraftPage />} /> */}
            <Route path="/vfr"          element={<VfrSearchPage />} />
            <Route path="/flight/:gufi" element={<FlightDetailPage />} />
          </Routes>
        </Suspense>
      </div>
      {!isOnboarding && <BottomNav />}
    </div>
  );
}

function App() {
  // Live-update theme when system preference changes and user is on 'auto'
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e) => {
      const pref = localStorage.getItem('theme') || 'auto';
      if (pref === 'auto') {
        document.documentElement.setAttribute('data-theme', e.matches ? 'light' : 'dark');
      }
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <StudentViewProvider>
          <AppShell />
        </StudentViewProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
